/**
 * Single source of truth for Optimizely feature flag keys.
 *
 * Add new experiments here by semantic name. Consumers reference flags via
 * `OPTIMIZELY_FLAGS.<NAME>` — never by index or raw string — so that
 * reordering, renaming, or removing an experiment is caught at compile
 * time across every call site.
 */
export const OPTIMIZELY_FLAGS = {
  HERO_TRIAL_PAYWALL: 'E023_V001_Hero the trial on the paywall',
  // GB only. Variation-1 offers the Gut Health Test add-on on ZOE Plus trial
  // checkouts, which suppress every upsell today. Trial baskets are exempt from
  // the commitment-plan rule, so monthly-renewal trials are in the treatment too.
  E036_V001_GHT_CHECKOUT_ADD_ON_FOR_TRIAL_PURCHASERS: 'E036_V001_GHT_CHECKOUT_ADD_ON_FOR_TRIAL_PURCHASERS',
  // UK only. Variation-1 offers the ZOE Plus membership on a £1 trial in place
  // of the standard plan pricing on /buymembership/plans.
  E034_V001_ONE_POUND_TRIAL: 'E034_V001_ONE_POUND_TRIAL',
  E042_V001_CHECKOUT_FUNNEL_SIMPLIFICATION: 'E042_V001_CHECKOUT_FUNNEL_SIMPLIFICATION',
  // Per-product funnel-simplification experiments, route-targeted in Optimizely
  // (V001 /buymembership, V002 /daily30, V003 /buyguthealthbar). Resolved by route
  // in lib/checkout/funnelSimplificationFlag.ts.
  E042_V002_CHECKOUT_FUNNEL_SIMPLIFICATION: 'E042_V002_CHECKOUT_FUNNEL_SIMPLIFICATION',
  E042_V003_CHECKOUT_FUNNEL_SIMPLIFICATION: 'E042_V003_CHECKOUT_FUNNEL_SIMPLIFICATION',
  // GB only. Variation-1 sells the 14-day trial at £1 while the paywall timer runs.
  E053_V001_PAYWALL_TIMER_FOR_TRIAL_DISCOUNT: 'E053_V001_PAYWALL_TIMER_FOR_TRIAL_DISCOUNT',
  // GB only. Variation-1 is the half-price paywall at /buymembership/plans-e055, rendered from its
  // ZoePlusSelectPlanExperiment record in Dato.
  E055_V001_HALVE_APP_MEMBERSHIP_PRICES: 'E055_V001_HALVE_APP_MEMBERSHIP_PRICES',
  // D30 + App cancellation flows. Variation-1 offers the discount to every reason but an
  // adverse reaction and adds the fallback levers to the non-price ones; 'original' keeps
  // the V1 chains, which shipped to everyone in August. See cancel/reasonRouting.ts.
  E035_V002_CANCELLATION_DISCOUNTS: 'E035_V002_Discounts_and_secondary_interventions_in_cancellation_flows',
  // Variation-1 shows a sticky footer on the Daily30 checkout page with a plan
  // selector and a single CTA to subscribe when the user scrolls past the plan
  // selection section.
  E003_V002_D30_STICKY_PURCHASE_FOOTER: 'E003_V002_D30_STICKY_PURCHASE_FOOTER',
  // GB only. Variation-1 sends the 'Gut Health Test' nav item and the GHT product-card
  // CTA to the GHT PDP instead of the ZOE App destination the two share today.
  E045_V001_GHT_USER_JOURNEY_NAVIGATION: 'E045_V001_GHT_USER_JOURNEY_NAVIGATION',
  // GB only. Variation-1 offers the Gut Health Test as a single-click cross-sell straight
  // after the Daily30 cross-sell that follows an app purchase; 'original' goes to order confirmation.
  E041_V001_POST_PURCHASE_GHT_CROSS_SELL: 'E041_V001_POST_PURCHASE_GHT_CROSS_SELL',
  // GB only. Variation-1 shows trial purchasers the post-purchase referral modal on order
  // confirmation, which paid-plan buyers already see; 'original' keeps it hidden from trials.
  E051_V001_REFERRAL_ON_TRIAL_ORDER_CONFIRMATION: 'E051_V001_REFERRAL_ON_TRIAL_ORDER_CONFIRMATION',
  // GB only. Variation-1 renders the Daily30 PDP from its Daily30PdpExperiment record in Dato,
  // which offers the 4-month plan as a welcome offer; 'original' renders the daily30Pdp record.
  E052_V001_DAILY30_4_MONTHS_INTRO_OFFER: 'E052_V001_DAILY30_4_MONTHS_INTRO_OFFER',
  // GB only. The single-click app trial cross-sell after a Daily30 or Bars purchase: variation-1
  // sells the trial price tagged with this flag in Stripe (free), 'original' the untagged £2.30 one.
  E054_V001_CROSS_SELL_FREE_APP_POST_PURCHASE_FOOD_PRODUCTS:
    'E054_V001_CROSS_SELL_FREE_APP_POST_PURCHASE_FOOD_PRODUCTS',
} as const

export type OptimizelyFlagKey = (typeof OPTIMIZELY_FLAGS)[keyof typeof OPTIMIZELY_FLAGS]

/**
 * Possible values returned by Optimizely for a flag (experiment variant).
 * Normalised from Optimizely dashboard names (e.g. 'Original' → 'original', 'Variation #1' → 'variation-1').
 */
export type OptimizelyFlagValue = 'original' | `variation-${number}`

/**
 * A record keyed by Optimizely flag values where 'original' is always required
 * and variation keys are optional.
 */
export type OptimizelyFlagRecord<T> = Pick<Record<OptimizelyFlagValue, T>, 'original'> &
  Partial<Record<`variation-${number}`, T>>
